interface ColumnConfig {
  id: string;
  name: string;
  type?: string;
  field?: string;
}

// Built-in columns already map to real lead fields; they must never be
// claimed as custom fields (otherwise "Status"/"Name" swallow CSV columns).
const isCustomColumn = (col: ColumnConfig) =>
  col.type !== 'default' && !String(col.id ?? '').startsWith('default-');

interface ParsedCSVData {
  headers: string[];
  rows: string[][];
}

/**
 * Full CSV parser: handles BOM, CRLF, quoted fields containing commas,
 * escaped quotes and newlines inside quoted fields.
 */
export const parseCSV = (csvText: string): ParsedCSVData => {
  const text = csvText.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  const records: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (char === '"') {
        if (next === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field.trim());
      field = '';
    } else if (char === '\n') {
      row.push(field.trim());
      field = '';
      if (row.some(c => c !== '')) records.push(row);
      row = [];
    } else {
      field += char;
    }
  }
  row.push(field.trim());
  if (row.some(c => c !== '')) records.push(row);

  if (records.length === 0) {
    throw new Error('CSV file is empty');
  }

  const headers = records[0];
  const rows = records.slice(1);

  return { headers, rows };
};

interface LeadImportData {
  lead_name: string;
  company?: string | null;
  email?: string | null;
  phone?: string | null;
  status?: string;
  priority?: string;
  value?: number | null;
  source?: string | null;
  conversation_notes?: string | null;
  next_action?: string | null;
  next_action_date?: string | null;
  custom_fields?: Record<string, any>;
}

const normalize = (s: string) => s.toLowerCase().trim().replace(/[\s_\-./]+/g, ' ');

const standardFieldMappings: Record<string, string> = {
  'name': 'lead_name',
  'full name': 'lead_name',
  'lead': 'lead_name',
  'lead name': 'lead_name',
  'leadname': 'lead_name',
  'contact': 'lead_name',
  'contact name': 'lead_name',
  'client': 'lead_name',
  'client name': 'lead_name',
  'customer': 'lead_name',
  'customer name': 'lead_name',
  'display name': 'lead_name',
  'company': 'company',
  'company name': 'company',
  'organization': 'company',
  'organisation': 'company',
  'organization name': 'company',
  'business': 'company',
  'business name': 'company',
  'account': 'company',
  'account name': 'company',
  'employer': 'company',
  'email': 'email',
  'e mail': 'email',
  'email address': 'email',
  'e mail address': 'email',
  'email 1 value': 'email',
  'primary email': 'email',
  'work email': 'email',
  'phone': 'phone',
  'phone number': 'phone',
  'telephone': 'phone',
  'mobile': 'phone',
  'mobile phone': 'phone',
  'cell': 'phone',
  'cell phone': 'phone',
  'phone 1 value': 'phone',
  'primary phone': 'phone',
  'work phone': 'phone',
  'home phone': 'phone',
  'status': 'status',
  'lead status': 'status',
  'priority': 'priority',
  'value': 'value',
  'deal value': 'value',
  'amount': 'value',
  'revenue': 'value',
  'notes': 'conversation_notes',
  'note': 'conversation_notes',
  'comments': 'conversation_notes',
  'description': 'conversation_notes',
  'conversation notes': 'conversation_notes',
  'source': 'source',
  'lead source': 'source',
  'next action': 'next_action',
  'action': 'next_action',
  'next step': 'next_action',
  'next action date': 'next_action_date',
  'action date': 'next_action_date',
  'follow up date': 'next_action_date',
};

// Headers whose values get combined into the lead name when no single
// name column exists (common in Google / Outlook / iPhone contact exports).
const firstNameHeaders = ['first name', 'firstname', 'given name', 'first'];
const middleNameHeaders = ['middle name', 'middle'];
const lastNameHeaders = ['last name', 'lastname', 'surname', 'family name', 'last'];

// --- Fuzzy matching for user-defined (custom) columns ---------------------

const levenshtein = (a: string, b: string): number => {
  if (a === b) return 0;
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const curr = [i];
    for (let j = 1; j <= n; j++) {
      curr[j] = Math.min(
        prev[j] + 1,
        curr[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
    prev = curr;
  }
  return prev[n];
};

// Words that add no meaning when comparing column names ("Home Address" ~ "Address").
const filler = new Set([
  'home', 'work', 'business', 'personal', 'primary', 'main', 'other',
  'mailing', 'physical', 'street', 'full', 'contact', 'the', 'my', '1', '2',
]);

const tokens = (s: string) => normalize(s).split(' ').filter(Boolean);
const coreTokens = (s: string) => {
  const t = tokens(s);
  const core = t.filter(w => !filler.has(w));
  return core.length ? core : t;
};

const similarity = (a: string, b: string) => {
  const max = Math.max(a.length, b.length);
  if (!max) return 0;
  return 1 - levenshtein(a, b) / max;
};

const scoreMatch = (header: string, columnName: string): number => {
  const h = normalize(header);
  const c = normalize(columnName);
  if (!h || !c) return 0;
  if (h === c) return 1;

  const hCore = coreTokens(h).sort().join(' ');
  const cCore = coreTokens(c).sort().join(' ');
  if (hCore === cCore) return 0.96;

  const hSet = new Set(coreTokens(h));
  const cSet = new Set(coreTokens(c));
  const overlap = [...cSet].filter(w => hSet.has(w)).length;
  if (overlap && (overlap === cSet.size || overlap === hSet.size)) return 0.9;

  if (h.includes(c) || c.includes(h)) return 0.88;

  return Math.max(similarity(h, c), similarity(hCore, cCore));
};

const FUZZY_THRESHOLD = 0.82;

const findCustomColumnMatch = (
  header: string,
  columnConfig: ColumnConfig[],
  used: Set<string>
): ColumnConfig | undefined => {
  let best: { col: ColumnConfig; score: number } | undefined;
  for (const col of columnConfig) {
    if (used.has(col.name)) continue;
    const score = scoreMatch(header, col.name);
    if (score >= FUZZY_THRESHOLD && (!best || score > best.score)) {
      best = { col, score };
    }
  }
  return best?.col;
};


export const mapCSVToLeads = (
  parsedData: ParsedCSVData,
  columnConfig: ColumnConfig[]
): LeadImportData[] => {
  const { headers, rows } = parsedData;

  columnConfig = (columnConfig || []).filter(isCustomColumn);

  const columnMapping: Array<{ index: number; field: string; isCustom: boolean }> = [];
  const usedCustomColumns = new Set<string>();

  const nameParts: { first: number[]; middle: number[]; last: number[] } = {
    first: [],
    middle: [],
    last: [],
  };

  // Pass 1: exact custom-column matches win before any fuzzy claim.
  headers.forEach((header, index) => {
    const h = normalize(header);
    if (!h || standardFieldMappings[h]) return;
    if (firstNameHeaders.includes(h) || middleNameHeaders.includes(h) || lastNameHeaders.includes(h)) return;
    const exact = columnConfig.find(col => normalize(col.name) === h);
    if (exact && !usedCustomColumns.has(exact.name)) {
      usedCustomColumns.add(exact.name);
      columnMapping.push({ index, field: exact.name, isCustom: true });
    }
  });

  headers.forEach((header, index) => {
    if (columnMapping.some(m => m.index === index)) return;

    const h = normalize(header);
    if (!h) return;

    if (firstNameHeaders.includes(h)) { nameParts.first.push(index); return; }
    if (middleNameHeaders.includes(h)) { nameParts.middle.push(index); return; }
    if (lastNameHeaders.includes(h)) { nameParts.last.push(index); return; }

    const standardField = standardFieldMappings[h];
    if (standardField) {
      columnMapping.push({ index, field: standardField, isCustom: false });
      return;
    }

    const customColumn = findCustomColumnMatch(h, columnConfig, usedCustomColumns);
    if (customColumn) {
      usedCustomColumns.add(customColumn.name);
      columnMapping.push({ index, field: customColumn.name, isCustom: true });
    }
  });


  const hasNameColumn =
    columnMapping.some(m => m.field === 'lead_name') ||
    nameParts.first.length > 0 ||
    nameParts.last.length > 0;

  if (!hasNameColumn) {
    throw new Error(
      `Couldn't find a name column. Add a column called "Name" (or "First Name" and "Last Name") to your file. Columns found: ${headers
        .filter(Boolean)
        .join(', ')}`
    );
  }

  const leads: LeadImportData[] = rows
    .filter(row => row.some(cell => cell.trim()))
    .map(row => {
      const lead: LeadImportData = { lead_name: '', custom_fields: {} };

      columnMapping.forEach(({ index, field, isCustom }) => {
        const value = row[index]?.trim() || '';
        if (!value) return;

        if (isCustom) {
          lead.custom_fields![field] = value;
        } else if (field === 'lead_name') {
          lead.lead_name = value;
        } else if (field === 'value') {
          const numValue = parseFloat(value.replace(/[^0-9.-]/g, ''));
          if (!isNaN(numValue)) lead.value = numValue;
        } else if (field === 'next_action_date') {
          const date = new Date(value);
          if (!isNaN(date.getTime())) lead.next_action_date = date.toISOString();
        } else {
          (lead as any)[field] = value;
        }
      });

      if (!lead.lead_name) {
        const pick = (idxs: number[]) =>
          idxs.map(i => row[i]?.trim() || '').filter(Boolean).join(' ');
        const combined = [pick(nameParts.first), pick(nameParts.middle), pick(nameParts.last)]
          .filter(Boolean)
          .join(' ')
          .trim();
        if (combined) lead.lead_name = combined;
      }

      // Last resort: a contact with only an email/company is still worth importing
      if (!lead.lead_name && lead.email) lead.lead_name = lead.email;
      if (!lead.lead_name && lead.company) lead.lead_name = lead.company;

      return lead;
    })
    .filter(lead => lead.lead_name);

  return leads;
};
